import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from './components/ui/sonner';
import { useAuthStore, useThemeStore, useFeaturesStore } from './stores';
import { Loader2 } from 'lucide-react';

// Pages
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DashboardPage from './pages/DashboardPage';
import ProductsPage from './pages/ProductsPage';
import ClientsPage from './pages/ClientsPage';
import QuotesPage from './pages/QuotesPage';
import QuoteDetailPage from './pages/QuoteDetailPage';
import EventsPage from './pages/EventsPage';
import ExpensesPage from './pages/ExpensesPage';
import SettingsPage from './pages/SettingsPage';
import CompanySettingsPage from './pages/CompanySettingsPage';
import CalendarPage from './pages/CalendarPage';
import TeamPage from './pages/TeamPage';
import InvoicesPage from './pages/InvoicesPage';
import LeadsPage from './pages/LeadsPage';
import PortalQuotePage from './pages/PortalQuotePage';
import DeliveriesPage from './pages/DeliveriesPage';
import PortalTourPage from './pages/PortalTourPage';
import ToursHistoryPage from './pages/ToursHistoryPage';
import PublicJoinPage from './pages/PublicJoinPage';
import PublicClientRegisterPage from './pages/PublicClientRegisterPage';
import PublicPortalLandingPage from './pages/PublicPortalLandingPage';
import DataManagementPage from './pages/DataManagementPage';
import PublicBookingPage from './pages/PublicBookingPage';

// Layout
import AppLayout from './components/layout/AppLayout';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes (offline cache)
      retry: (failureCount, error) => {
        if (error?.message?.includes('401') || error?.message?.includes('Token')) {
          return false;
        }
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
    },
  },
});

const ProtectedRoute = ({ children }) => {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return children;
};

const PublicRoute = ({ children }) => {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  if (isAuthenticated) return <Navigate to="/" replace />;
  return children;
};

const FeatureRoute = ({ feature, children }) => {
  const enabled = useFeaturesStore((s) => s[feature]);
  const loaded = useFeaturesStore((s) => s.loaded);
  if (!loaded) return null;
  if (!enabled) return <Navigate to="/" replace />;
  return children;
};

function App() {
  const theme = useThemeStore((s) => s.theme);
  const loadFeatures = useFeaturesStore((s) => s.loadFeatures);
  
  const checkAuth = useAuthStore((s) => s.checkAuth);
  const [isHydrated, setIsHydrated] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  // 1. Gestion de l'hydratation (Chargement depuis localStorage)
  useEffect(() => {
    // Fonction utilitaire pour éviter la duplication
    const finishHydration = () => setIsHydrated(true);

    // Si le store est déjà prêt (cas fréquent au refresh)
    if (useAuthStore.persist.hasHydrated()) {
      finishHydration();
    } else {
      // Sinon on attend l'événement
      const unsub = useAuthStore.persist.onFinishHydration(() => finishHydration());
      return () => unsub();
    }
  }, []);

  // 2. Vérification de la session une fois hydraté
  useEffect(() => {
    if (isHydrated) {
      const verifySession = async () => {
        try {
          // IMPORTANT: On vérifie directement dans l'état si un token existe
          // avant de lancer la vérification serveur.
          const token = useAuthStore.getState().token;

          if (token) {
            // Si on a un token, on le vérifie auprès du serveur
            await checkAuth();
          }
          // Si pas de token, on ne fait rien (on reste déconnecté), 
          // mais on arrête le chargement.
        } catch (error) {
          console.error("Erreur init auth:", error);
        } finally {
          setIsChecking(false);
        }
      };
      
      verifySession();
    }
  }, [isHydrated, checkAuth]);

  // 3. Gestion Thème & Features
  useEffect(() => {
    loadFeatures();
    const root = document.documentElement;
    if (theme === 'dark') root.classList.add('dark');
    else root.classList.remove('dark');
  }, [theme, loadFeatures]);

  // 4. Écran de chargement
  if (!isHydrated || isChecking) {
    return (
      <div className="min-h-screen w-full flex flex-col items-center justify-center bg-background">
        <Loader2 className="w-10 h-10 animate-spin text-primary mb-4" />
        <p className="text-muted-foreground text-sm font-medium">Chargement de Votre espace...</p>
      </div>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <div className="min-h-screen bg-background">
        <BrowserRouter>
          <Routes>
            {/* Portal Routes */}
            <Route path="/portal" element={<PublicPortalLandingPage />} />
            <Route path="/join" element={<PublicJoinPage />} />
            <Route path="/register" element={<PublicClientRegisterPage />} />
            <Route path="/booking" element={<PublicBookingPage />} />
            
            {/* Public Auth Routes */}
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <LoginPage />
                </PublicRoute>
              }
            />
            <Route
              path="/auth/register"
              element={
                <PublicRoute>
                  <RegisterPage />
                </PublicRoute>
              }
            />
            <Route
              path="/forgot-password"
              element={
                <PublicRoute>
                  <ForgotPasswordPage />
                </PublicRoute>
              }
            />
            <Route
              path="/reset-password"
              element={
                <PublicRoute>
                  <ResetPasswordPage />
                </PublicRoute>
              }
            />
            <Route path="/portal/tour/:token" element={<PortalTourPage />} />
            <Route path="/portal/quote/:token" element={<PortalQuotePage />} />
            
            {/* Protected App Routes */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <AppLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<DashboardPage />} />
              <Route path="data" element={<DataManagementPage />} />
              <Route path="products" element={<ProductsPage />} />
              <Route path="clients" element={<ClientsPage />} />
              <Route path="quotes" element={<QuotesPage />} />
              <Route path="quotes/:id" element={<QuoteDetailPage />} />
              <Route path="invoices" element={<InvoicesPage />} />
              <Route path="events" element={<EventsPage />} />
              <Route path="calendar" element={<CalendarPage />} />
              <Route path="expenses" element={<ExpensesPage />} />
              <Route path="leads" element={<FeatureRoute feature="crm"><LeadsPage /></FeatureRoute>} />
              <Route path="team" element={<TeamPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="settings/company" element={<CompanySettingsPage />} />
              <Route path="deliveries" element={<DeliveriesPage />} />
              <Route path="deliveries/history" element={<ToursHistoryPage />} />
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster richColors position="top-right" />
      </div>
    </QueryClientProvider>
  );
}

export default App;